import React, { useEffect, useState } from "react";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation, getLocalizedValue } from "../../utils/translate.js";
import { formatTime } from "../../utils/dateUtils.js";
import { DELIVERED_UNDO_MS } from "../../utils/orderBoard.js";

const MAX_DISHES = 4;

const formatShortDate = (dt) => {
    const [datePart] = String(dt).split("T");
    const [, m, d] = datePart.split("-");
    return m && d ? `${d}.${m}` : "";
};

const remainingMs = (startedAt, durationMs) => Math.max(0, startedAt + durationMs - Date.now());

/** Progress bar that must not re-render on countdown ticks, or the CSS animation restarts. */
const UndoTimerBar = React.memo(({ startedAt, durationMs }) => {
    const left = remainingMs(startedAt, durationMs);
    const startScale = durationMs > 0 ? left / durationMs : 0;
    return (
        <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-1 overflow-hidden rounded-t-[5px] bg-orange-100 dark:bg-orange-950/60"
        >
            <div
                className="h-full w-full bg-customOrange dark:bg-orange-400"
                style={{
                    transformOrigin: "left center",
                    transform: `scaleX(${startScale})`,
                    animation: `boardUndoShrink ${left}ms linear forwards`
                }}
            />
        </div>
    );
});

const useUndoSeconds = (startedAt, durationMs, enabled) => {
    const [secs, setSecs] = useState(() => (enabled ? Math.ceil(remainingMs(startedAt, durationMs) / 1000) : 0));
    useEffect(() => {
        if (!enabled || startedAt == null) {
            setSecs(0);
            return undefined;
        }
        const tick = () => setSecs(Math.ceil(remainingMs(startedAt, durationMs) / 1000));
        tick();
        const id = setInterval(tick, 200);
        return () => clearInterval(id);
    }, [enabled, startedAt, durationMs]);
    return secs;
};

/**
 * Kanban card: order number, delivery time and dishes. No payment info.
 */
const BoardCard = ({
    order,
    isNew,
    isPending,
    undoStartedAt,
    showDate,
    nextStatusLabel,
    onOpen,
    onMoveNext,
    onRevert,
    onDragStart,
    onDragEnd
}) => {
    const { language } = useLanguage();
    const dishes = Array.isArray(order.orderDishList) ? order.orderDishList : [];
    const shown = dishes.slice(0, MAX_DISHES);
    const hiddenCount = dishes.length - shown.length;
    const totalQty = dishes.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);
    const isUndoable = undoStartedAt != null;
    const undoSecs = useUndoSeconds(undoStartedAt, DELIVERED_UNDO_MS, isUndoable);
    const delivered = order.status === "DELIVERED" && !isUndoable;
    const undoLabel = getTranslation("orderBoardPage.undo", language);

    return (
        <div
            draggable={!isPending}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onClick={onOpen}
            onKeyDown={(e) => {
                if (e.key === "Enter") onOpen();
            }}
            role="button"
            tabIndex={0}
            aria-label={`${getTranslation("orderPage.orderNumber", language)} ${order.number ?? ""}`}
            className={`group relative cursor-grab select-none rounded-md border bg-white p-2.5 text-left shadow-sm transition active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 dark:bg-[#161b22] ${
                isUndoable
                    ? "border-customOrange dark:border-orange-400"
                    : isNew
                      ? "border-2 border-blue-500 dark:border-[#388bfd]"
                      : "border-gray-200 hover:border-gray-400 dark:border-[#30363d] dark:hover:border-[#8b949e]"
            } ${isPending ? "opacity-60" : ""} ${delivered ? "opacity-70" : ""}`}
        >
            {isUndoable && <UndoTimerBar startedAt={undoStartedAt} durationMs={DELIVERED_UNDO_MS} />}
            <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-sm font-bold text-gray-900 dark:text-[#e6edf3]">
                        #{order.number ?? "—"}
                    </span>
                    {isNew && (
                        <span className="shrink-0 rounded bg-blue-600 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-white dark:bg-[#1f6feb]">
                            {getTranslation("orderBoardPage.newLabel", language)}
                        </span>
                    )}
                </span>
                <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-700 dark:text-[#c9d1d9]">
                    {showDate && <span className="mr-1 font-normal text-gray-500 dark:text-[#8b949e]">{formatShortDate(order.deliveryDateTime)}</span>}
                    {formatTime(order.deliveryDateTime)}
                </span>
            </div>

            {shown.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                    {shown.map((item, idx) => (
                        <li
                            key={item?.id ?? item?.dishDto?.id ?? idx}
                            className="flex gap-1.5 text-xs leading-snug text-gray-700 dark:text-[#c9d1d9]"
                        >
                            <span className="shrink-0 font-semibold tabular-nums text-gray-900 dark:text-[#e6edf3]">
                                {item?.quantity ?? 1}×
                            </span>
                            <span className="min-w-0 break-words">
                                {getLocalizedValue(item?.dishDto, "name", language) || "—"}
                            </span>
                        </li>
                    ))}
                    {hiddenCount > 0 && (
                        <li className="text-xs text-gray-500 dark:text-[#8b949e]">
                            +{hiddenCount} {getTranslation("orderBoardPage.more", language)}
                        </li>
                    )}
                </ul>
            )}

            <div className="mt-2 flex items-center justify-between gap-2 border-t border-gray-100 pt-1.5 dark:border-[#21262d]">
                <span className="text-[11px] tabular-nums text-gray-500 dark:text-[#8b949e]">
                    {totalQty} {getTranslation("orderBoardPage.portions", language)}
                </span>
                {isUndoable ? (
                    <button
                        type="button"
                        disabled={isPending}
                        onClick={(e) => {
                            e.stopPropagation();
                            onRevert();
                        }}
                        title={undoLabel}
                        aria-label={undoLabel}
                        className="rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums text-customOrange hover:bg-orange-50 disabled:opacity-40 dark:text-orange-300 dark:hover:bg-orange-950/40"
                    >
                        {undoLabel}
                        {undoSecs > 0 ? ` ${undoSecs}` : ""}
                    </button>
                ) : (
                    nextStatusLabel && (
                        <button
                            type="button"
                            disabled={isPending}
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveNext();
                            }}
                            title={`${getTranslation("orderBoardPage.moveTo", language)}: ${nextStatusLabel}`}
                            aria-label={`${getTranslation("orderBoardPage.moveTo", language)}: ${nextStatusLabel}`}
                            className="rounded px-1.5 py-0.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 dark:text-[#8b949e] dark:hover:bg-[#21262d] dark:hover:text-[#e6edf3]"
                        >
                            →
                        </button>
                    )
                )}
            </div>
        </div>
    );
};

export default React.memo(BoardCard);
